// src/components/store/sections/hero-banner/HeroMinimalCenter.tsx
"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Flame } from "lucide-react";

export interface HeroBannerItem {
  id: number;
  badge_title?: string;
  title: string;
  subtitle?: string;
  button_text?: string;
  target_url?: string;
  image?: string | null;
  font_family?: string;
  title_color?: string;
  subtitle_color?: string;
  bg_color?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_direction?: string;
}

interface HeroBannerProps {
  banners?: HeroBannerItem[];
  primaryColor?: string;
}

export default function HeroMinimalCenter({
  banners = [],
  primaryColor = "#059669",
}: HeroBannerProps) {
  const banner = banners?.[0] || null;

  // 🌟 ব্যাকএন্ড সলিড অথবা ২/৩ কালার গ্রেডিয়েন্ট জেনারেটর
  const getBannerBackgroundStyle = (item?: HeroBannerItem | null) => {
    if (!item) return {};
    const dir = item.gradient_direction || "to bottom right";
    const c1 = item.gradient_color_1;
    const c2 = item.gradient_color_2;
    const c3 = item.gradient_color_3;

    if (c1 && c2 && c3) {
      return { background: `linear-gradient(${dir}, ${c1}, ${c2}, ${c3})` };
    }
    if (c1 && c2) {
      return { background: `linear-gradient(${dir}, ${c1}, ${c2})` };
    }
    if (item.bg_color) {
      return { backgroundColor: item.bg_color };
    }
    return {};
  };

  const formatImageUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const imageUrl = banner ? formatImageUrl(banner.image) : null;
  const bannerFont = banner?.font_family || "Inter, sans-serif";
  const customBgStyle = getBannerBackgroundStyle(banner);

  if (!banner) {
    return null;
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
      {/* 🌟 ২০টি ফন্টের Google Fonts CDN লিঙ্ক */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Anek+Bangla:wght@400;600;800&family=Bebas+Neue&family=Black+Ops+One&family=Cinzel:wght@600;800&family=Galada&family=Hind+Siliguri:wght@400;600;700&family=Inter:wght@400;700;900&family=Mina:wght@400;700&family=Montserrat:wght@400;700;900&family=Noto+Sans+Bengali:wght@400;700&family=Oswald:wght@500;700&family=Playfair+Display:wght@600;800&family=Plus+Jakarta+Sans:wght@600;800&family=Poppins:wght@400;600;800&family=Roboto:wght@400;700&family=Syne:wght@700;800&display=swap"
        rel="stylesheet"
      />

      <div
        style={customBgStyle}
        className="relative w-full rounded-3xl overflow-hidden border-2 border-slate-200 dark:border-slate-800 bg-slate-900 text-white min-h-[440px] flex items-center justify-center text-center p-6 sm:p-14 shadow-2xl transition-all"
      >
        {/* Background Soft Ambient Blur Image */}
        {imageUrl && (
          <div className="absolute inset-0 z-0">
            <Image
              src={imageUrl}
              alt={banner.title || "Banner"}
              fill
              unoptimized
              priority
              className="object-cover opacity-30 blur-[2px] scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40" />
          </div>
        )}

        {/* Centered 3D Glassmorphism Box */}
        <div className="relative z-10 max-w-2xl mx-auto space-y-5 backdrop-blur-md bg-slate-900/50 border border-white/10 p-8 sm:p-12 rounded-3xl shadow-2xl">
          {banner.badge_title && (
            <span
              style={{
                backgroundColor: `${primaryColor}25`,
                color: primaryColor,
                borderColor: `${primaryColor}40`,
              }}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider border"
            >
              <Flame className="w-3.5 h-3.5 text-amber-400" />
              <span>{banner.badge_title}</span>
            </span>
          )}

          <h1
            style={{
              fontFamily: bannerFont,
              color: banner.title_color || "#ffffff",
            }}
            className="text-3xl sm:text-5xl font-black tracking-tight leading-tight"
          >
            {banner.title || "Elevate Your Lifestyle"}
          </h1>

          <p
            style={{
              fontFamily: bannerFont,
              color: banner.subtitle_color || "#cbd5e1",
            }}
            className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto font-medium leading-relaxed"
          >
            {banner.subtitle || "Discover the finest curated items crafted with utmost perfection and durability."}
          </p>

          <div className="pt-3">
            <Link
              href={banner.target_url || "/products"}
              style={{ backgroundColor: primaryColor }}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-white text-xs sm:text-sm font-black shadow-xl hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            >
              <span>{banner.button_text || "Explore Now"}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}